<script setup lang="ts">
import { Check, User, Users, Video, ShieldCheck, RotateCcw, Clock } from 'lucide-vue-next'
import InitialsAvatar from '@/Components/ui/InitialsAvatar.vue'

interface Tutor {
  name: string
  avatar?: string
  subjects: Array<{ id: number; name: string }>
}

interface Tier {
  slug: string
  name: string
  description: string
  features: string[]
  price: number
}

interface Props {
  tutor: Tutor
  tier: Tier
  videosCount?: number
}

const props = defineProps<Props>()

const tierConfig = {
  base: {
    icon: Video,
    label: 'CONTENT',
    gradient: 'from-gray-50 to-gray-100',
    iconBg: 'bg-gray-500',
    priceColor: 'text-gray-700',
    badgeGradient: 'bg-gray-600',
    checkBg: 'bg-gray-100',
    checkColor: 'text-gray-600',
  },
  silver: {
    icon: Users,
    label: 'POPULAR',
    gradient: 'from-teal-50 to-teal-100',
    iconBg: 'bg-blue-500',
    priceColor: 'text-blue-700',
    badgeGradient: 'bg-gradient-to-r from-teal-500 to-teal-800',
    checkBg: 'bg-blue-100',
    checkColor: 'text-blue-600',
  },
  gold: {
    icon: User,
    label: 'PREMIUM',
    gradient: 'from-teal-50 to-teal-100',
    iconBg: 'bg-gradient-to-r from-teal-500 to-teal-800',
    priceColor: 'text-yellow-700',
    badgeGradient: 'bg-gradient-to-r from-teal-500 to-teal-800',
    checkBg: 'bg-yellow-100',
    checkColor: 'text-yellow-600',
  },
}

const config = tierConfig[props.tier.slug as keyof typeof tierConfig] || tierConfig.base

// Calculate VAT (20%)
const subtotal = props.tier.price / 1.2
const vat = props.tier.price - subtotal

const subjectsDisplay = props.tutor.subjects.slice(0, 2).map(s => s.name).join(' & ')
</script>

<template>
  <!-- Not pinned: a tall summary pinned under a sticky header covers the form
       it is summarising on any short viewport. -->
  <section class="bg-white rounded-2xl shadow-sm p-6">
    <h3 class="text-xl font-semibold text-gray-900 mb-6">Order Summary</h3>

    <!-- Tutor Info -->
    <div class="mb-6">
      <div class="flex items-center space-x-3 mb-4">
        <InitialsAvatar
          :name="tutor.name"
          :src="tutor.avatar"
          size-class="size-16"
          rounded="rounded-xl"
        />
        <div>
          <h4 class="font-semibold text-gray-900">{{ tutor.name }}</h4>
          <p class="text-sm text-gray-600">{{ subjectsDisplay }}</p>
        </div>
      </div>
    </div>

    <!-- Tier Card -->
    <div :class="['bg-gradient-to-br rounded-xl p-6 mb-6', config.gradient]">
      <div class="flex items-center justify-between mb-3">
        <div class="flex items-center space-x-2">
          <div :class="['w-10 h-10 rounded-lg flex items-center justify-center', config.iconBg]">
            <component :is="config.icon" class="w-5 h-5 text-white" />
          </div>
          <div>
            <h4 class="font-bold text-gray-900">{{ tier.name }}</h4>
            <p class="text-xs text-gray-600">One lesson a week</p>
          </div>
        </div>
        <span :class="['text-white px-3 py-1 rounded-full text-xs font-bold', config.badgeGradient]">
          {{ config.label }}
        </span>
      </div>
      <div :class="['text-3xl font-bold mb-2', config.priceColor]">
        £{{ tier.price.toFixed(2) }}<span class="text-sm text-gray-600 font-normal">/lesson</span>
      </div>
    </div>

    <!-- Features -->
    <div class="space-y-4 mb-6 pb-6 border-b border-gray-200">
      <h4 class="font-semibold text-gray-900 text-sm">What's Included:</h4>
      <div class="space-y-3">
        <div
          v-for="(feature, idx) in tier.features"
          :key="idx"
          class="flex items-start space-x-3"
        >
          <div :class="['w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5', config.checkBg]">
            <Check :class="['w-3 h-3', config.checkColor]" />
          </div>
          <div>
            <p class="text-sm font-medium text-gray-900">{{ feature }}</p>
          </div>
        </div>
      </div>
    </div>

    <!-- Price Breakdown -->
    <div class="space-y-3 mb-6">
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-600">Subtotal</span>
        <span class="font-semibold text-gray-900">£{{ subtotal.toFixed(2) }}</span>
      </div>
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-600">VAT (20%)</span>
        <span class="font-semibold text-gray-900">£{{ vat.toFixed(2) }}</span>
      </div>
      <div class="border-t border-gray-200 pt-3">
        <div class="flex items-center justify-between">
          <span class="text-lg font-bold text-gray-900">Total Due</span>
          <span :class="['text-2xl font-bold', config.priceColor]">£{{ tier.price.toFixed(2) }}</span>
        </div>
        <p class="text-xs text-gray-500 mt-1">For the first lesson, taken when your tutor accepts</p>
      </div>
    </div>

    <!-- Trust Indicators -->
    <div class="space-y-3">
      <div class="flex items-center text-xs text-gray-600">
        <ShieldCheck class="w-4 h-4 text-green-600 mr-2" />
        <span>Nothing is charged until your tutor accepts</span>
      </div>
      <div class="flex items-center text-xs text-gray-600">
        <RotateCcw class="w-4 h-4 text-blue-600 mr-2" />
        <span>No commitment: end your lessons at any time</span>
      </div>
      <div class="flex items-center text-xs text-gray-600">
        <Clock class="w-4 h-4 text-purple-600 mr-2" />
        <span>Each lesson is charged 48 hours before it</span>
      </div>
    </div>
  </section>
</template>
